import { Divider, Spin } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import useAPIQuery from '../api/useAPIQuery';
import Config from '../app/config';
import CandidateManagerFeedback from '../components/ApplicantAnalysis/TestsTab/CandidateManagerFeedback';
import TestFitCultural from '../components/ApplicantAnalysis/TestsTab/TestFitCultural';
import TestInterests from '../components/ApplicantAnalysis/TestsTab/TestInterests';
import TestMatch from '../components/ApplicantAnalysis/TestsTab/TestMatch';
import TestProfileMap from '../components/ApplicantAnalysis/TestsTab/TestProfileMap';
import TestSkills from '../components/ApplicantAnalysis/TestsTab/TestSkills';
import TestStatusTable from '../components/ApplicantAnalysis/TestsTab/TestStatusTable';
import ErrorResult from '../components/ErrorResult';
import useTypedParams, { Optional } from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import {
  CandidateId,
  JobPostingId,
  MindmatchAttributes,
  MindmatchExternalResultScore,
} from '../types';

type AssessmentTypes =
  | 'OPERATIONAL'
  | 'INTERESTS'
  | 'MOTIVATIONAL'
  | 'SJT'
  | 'OCP'
  | 'PROFILEMAP'
  | 'LOGIC';

export interface AssessmentSentTable {
  name: string;
  type: AssessmentTypes;
  sentAt?: string;
  finished?: boolean;
}

const CandidateTests = () => {
  const { t } = useTranslation();

  const { id, applicant_id } = useTypedParams({
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const functions = useAPIQuery('/mindsight/ats-integrations/functions/');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`, { enabled: !!id });

  const recruitmentProcess = useAPIQuery(
    [
      '/recruitment-processes/',
      {
        params: {
          candidate: applicant_id,
          job_posting: id,
        },
      },
    ],
    {
      enabled: !!id,
      select(data) {
        return !!id && data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const testResults = useAPIQuery([
    '/test-results/',
    {
      params: {
        candidate: applicant_id,
        recruitment_process: recruitmentProcess.data?.id,
        recent: id ? undefined : true,
      },
    },
  ]);

  const matchResults = useAPIQuery(
    [
      '/match-results/',
      {
        params: {
          candidate: applicant_id,
          recruitment_process: recruitmentProcess.data?.id,
          function_id: jobPosting.data?.function_id || undefined,
        },
      },
    ],
    {
      select(data) {
        return data.count > 0 ? data.results[0] : null;
      },
      enabled: !!jobPosting.data?.function_id,
    }
  );

  const matchResultForComparison = useAPIQuery(
    [
      '/match-results/',
      {
        params: {
          candidate: applicant_id,
          recruitment_process: recruitmentProcess.data?.id,
          function_id: searchParams.get('functionCompareId')
            ? parseInt(searchParams.get('functionCompareId') || '', 10)
            : undefined,
          recent: id ? undefined : true,
        },
      },
    ],
    {
      select(data) {
        return data.count > 0 ? data.results[0] : null;
      },
      enabled: !!searchParams.get('functionCompareId'),
    }
  );

  const emailCommunications = useAPIQuery([
    '/email-communications/',
    {
      params: {
        candidate: applicant_id,
        job_posting: id,
        status: 'SENT',
        type: 'TESTS',
      },
    },
  ]);

  let functionName;
  let matchScore;
  let managerFeedbackLink;

  const logicScore =
    testResults.data?.results.find((testResult) => testResult.type === 'LOGIC')?.primary_score ??
    undefined;
  const sjtScore =
    testResults.data?.results.find((testResult) => testResult.type === 'SJT')?.primary_score ??
    undefined;
  const motivationalScore =
    testResults.data?.results.find((testResult) => testResult.type === 'MOTIVATIONAL')
      ?.primary_score ?? undefined;
  const rawPotentialScore =
    testResults.data?.results.find((testResult) => testResult.type === 'RAW_POTENTIAL')
      ?.primary_score ?? undefined;
  const ocpScore =
    testResults.data?.results.find((testResult) => testResult.type === 'OCP')?.primary_score ??
    undefined;

  const profileMapScore = (testResults.data?.results.find(
    (testResult) => testResult.type === 'PROFILEMAP'
  )?.secondary_score || []) as MindmatchAttributes[];
  const attributeScore = (testResults.data?.results.find(
    (testResult) => testResult.type === 'ATTRIBUTES'
  )?.secondary_score || []) as MindmatchAttributes[];
  const interestScore = (testResults.data?.results.find(
    (testResult) => testResult.type === 'INTERESTS'
  )?.secondary_score || {}) as MindmatchExternalResultScore;

  if (id) {
    functionName = functions.data?.results.find(
      (fn) => fn.id === jobPosting.data?.function_id
    )?.name;

    matchScore = matchResults.data?.score;
  }

  let candidateAttributes = profileMapScore;
  let hasAttributes = false;

  if (attributeScore.length > 0) {
    candidateAttributes = attributeScore;
    hasAttributes = true;
  }

  const candidateAssessmentSent: AssessmentSentTable[] = [
    {
      name: t('Simplificado de Raciocínio'),
      type: 'OPERATIONAL',
    },
    {
      name: t('Interesses Profissionais'),
      type: 'INTERESTS',
    },
    {
      name: t('Inferências (Motivacional)'),
      type: 'MOTIVATIONAL',
    },
    {
      name: t('Situacional (Social)'),
      type: 'SJT',
    },
    {
      name: t('Fit Cultural'),
      type: 'OCP',
    },
    {
      name: t('Perfil'),
      type: 'PROFILEMAP',
    },
    {
      name: t('Adaptativo de Raciocínio (Mental)'),
      type: 'LOGIC',
    },
  ];

  emailCommunications.data?.results.map((email) => {
    email.tests?.map((testType) => {
      const existingAssessmentSentIndex = candidateAssessmentSent.findIndex(
        (obj) => obj.type === testType
      );
      // findIndex() returns -1 when it does not find the matching statement
      if (existingAssessmentSentIndex !== -1) {
        const existingAssessmentSentEntry = candidateAssessmentSent[existingAssessmentSentIndex];
        // Check if both dates exist
        if (existingAssessmentSentEntry.sentAt && email.sent_at) {
          const existingSentDateTime = new Date(existingAssessmentSentEntry.sentAt);
          const newSentDateTime = new Date(email.sent_at);
          // Keep most recent date in the table
          if (newSentDateTime > existingSentDateTime) {
            candidateAssessmentSent[existingAssessmentSentIndex].sentAt = email.sent_at;
          }
          // If one of the dates does not exist, it will try to use an existing one in the table
        } else {
          candidateAssessmentSent[existingAssessmentSentIndex].sentAt =
            existingAssessmentSentEntry.sentAt || email.sent_at || undefined;
        }
      }
    });
  });

  if (testResults.data?.results[0]) {
    managerFeedbackLink = `${Config.REACT_APP_MINDSIGHT_SYSTEM_URL}devolutiva/${testResults.data.results[0].mindsight_candidate_id}/devolutiva_gestor/?code=${testResults.data.results[0].mindsight_candidate_uuid}`;
  }

  return (
    <ErrorResult
      error={
        candidate.error ||
        jobPosting.error ||
        functions.error ||
        recruitmentProcess.error ||
        testResults.error ||
        matchResults.error ||
        matchResultForComparison.error ||
        emailCommunications.error
      }
    >
      <Helmet>
        <title>
          {t('Testes')} | {candidate.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Spin
        spinning={
          candidate.isLoading ||
          jobPosting.isLoading ||
          functions.isLoading ||
          recruitmentProcess.isLoading ||
          testResults.isLoading ||
          matchResults.isLoading ||
          matchResultForComparison.isLoading ||
          emailCommunications.isLoading
        }
      >
        <TestStatusTable
          loading={testResults.isFetching || emailCommunications.isFetching}
          results={testResults.data?.results}
          tableRows={candidateAssessmentSent}
        />
        <Divider />
        <TestFitCultural grade={ocpScore} />
        <Divider />
        <TestMatch
          archetypeName={functionName}
          archetypeMatch={matchScore}
          rawPotential={rawPotentialScore}
          selectValue={
            functions.data?.results.find(
              (fn) => fn.id === parseInt(searchParams.get('functionCompareId') || '0', 10)
            )?.name
          }
          comparatorOptions={functions.data?.results
            .map((fn) => ({
              label: fn.name,
              value: fn.id,
            }))
            .sort((a, b) => (a.label > b.label ? 1 : -1))}
          onChangeFunction={(id) => {
            searchParams.set('functionCompareId', id || '');
            setSearchParams(searchParams);
          }}
          compareGrade={matchResultForComparison.data?.score}
        />
        <Divider />
        <TestSkills mental={logicScore} social={sjtScore} motivational={motivationalScore} />
        {testResults.isLoading || candidateAttributes.length !== 0 ? (
          <>
            <Divider />
            <TestProfileMap attributesData={candidateAttributes} hasAttributes={hasAttributes} />
          </>
        ) : null}
        {testResults.isLoading || Object.keys(interestScore).length !== 0 ? (
          <>
            <Divider />
            <TestInterests interestScore={interestScore} />
          </>
        ) : null}
        {testResults.isLoading || managerFeedbackLink ? (
          <>
            <Divider />
            <CandidateManagerFeedback managerFeedbackLink={managerFeedbackLink} />
          </>
        ) : null}
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(CandidateTests);
